import React, { useEffect, useState, createContext, useContext } from 'react';
type Story = {
  id: string;
  title: string;
  content: string;
  createdAt: Date;
};
type StoryContextType = {
  storyElements: {
    responses: string[];
    currentQuestion: number;
  };
  addResponse: (response: string) => void;
  isStoryComplete: boolean;
  setIsStoryComplete: (complete: boolean) => void;
  showEbook: boolean;
  setShowEbook: (show: boolean) => void;
  generateStory: () => string;
  savedStories: Story[];
  saveStory: (title: string, content: string) => void;
  previewStory: Story | null;
  setPreviewStory: (story: Story | null) => void;
  isLoggedIn: boolean;
  setIsLoggedIn: (loggedIn: boolean) => void;
  tempStory: {
    title: string;
    content: string;
  } | null;
  setTempStory: (story: {
    title: string;
    content: string;
  } | null) => void;
};
const StoryContext = createContext<StoryContextType | undefined>(undefined);
export const StoryProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [storyElements, setStoryElements] = useState({
    responses: [],
    currentQuestion: 0
  });
  const [isStoryComplete, setIsStoryComplete] = useState(false);
  const [showEbook, setShowEbook] = useState(false);
  const [savedStories, setSavedStories] = useState<Story[]>([{
    id: 'space-cupcake-1',
    title: 'Cupcake in Space!',
    coverImage: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9XCTvsA9WeFLGwuLASQLcC/ChatGPT_Image_May_13%2C_2025_at_05_12_16_PM.png',
    content: `Once upon a time, there was a brave little cupcake who dreamed of exploring the stars. With pink frosting and a cherry on top, this cupcake was different from all the others in the bakery.
One magical day, the cupcake discovered a tiny rocket ship just their size! Without hesitation, they climbed aboard, put on their space helmet, and blasted off into the twinkling cosmos.
As they zoomed past planets and danced with shooting stars, the cupcake made friends with meteors and shared stories with satellites. They even discovered that the moon wasn't made of cheese after all - it was made of sprinkles!
The little cupcake's adventure became legendary in the bakery, proving that even the sweetest treats can have the biggest dreams. And whenever anyone looked up at the night sky, they would smile, knowing that somewhere up there, a brave little cupcake was having the time of their life.
The End`,
    createdAt: new Date()
  }]);
  const [previewStory, setPreviewStory] = useState<Story | null>(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [tempStory, setTempStory] = useState<{
    title: string;
    content: string;
  } | null>(null);
  useEffect(() => {
    const accessToken = localStorage.getItem('access_token');
    const userEmail = localStorage.getItem('user_email');
    if (accessToken && userEmail) {
      setIsLoggedIn(true);
    }
  }, []);
  const questions = ['Who is the main character of your story?', 'What does your character look like?', 'Where does your character live?', "What is your character's favorite thing to do?", 'One day, something special happened. What was it?', 'How did your character feel about this?', 'Who did your character meet on their adventure?', 'What funny thing happened next?', 'How did your character solve the problem?', 'What did your character learn from this adventure?'];
  const addResponse = (response: string) => {
    setStoryElements(prev => ({
      ...prev,
      responses: [...prev.responses, response],
      currentQuestion: prev.currentQuestion + 1
    }));
  };
  const generateStory = () => {
    const [character, appearance, location, hobby, event, feeling, friend, funny, solution, lesson] = storyElements.responses;
    return `
      Once upon a time, there was ${character}. ${character} was ${appearance} and lived in ${location}.
      More than anything else, ${character} loved to ${hobby}. 
      One special day, ${event}! ${character} felt ${feeling} about this amazing surprise.
      During this adventure, ${character} met ${friend}. Together, they had so much fun when ${funny}!
      When things got tricky, ${character} knew just what to do. ${solution}
      At the end of this wonderful adventure, ${character} learned something very important: ${lesson}
      And they all lived happily ever after!
    `.trim();
  };
  const saveStory = (title: string, content: string) => {
    const newStory: Story = {
      id: Date.now().toString(),
      title,
      content,
      createdAt: new Date()
    };
    setSavedStories(prev => [...prev, newStory]);
  };
  return <StoryContext.Provider value={{
    storyElements,
    addResponse,
    isStoryComplete,
    setIsStoryComplete,
    showEbook,
    setShowEbook,
    generateStory,
    savedStories,
    saveStory,
    previewStory,
    setPreviewStory,
    isLoggedIn,
    setIsLoggedIn,
    tempStory,
    setTempStory
  }} data-id="element-901">
      {children}
    </StoryContext.Provider>;
};
export const useStory = () => {
  const context = useContext(StoryContext);
  if (context === undefined) {
    throw new Error('useStory must be used within a StoryProvider');
  }
  return context;
};